import { Children, isValidElement, type ReactNode } from 'react';

export type AssistantBubbleProps = {
    readonly children: ReactNode;
    readonly timestamp?: number;
    readonly timeFormatter?: Intl.DateTimeFormat;
    readonly compact?: boolean;
    readonly streaming?: boolean;
};

export type AssistantBubbleTextProps = {
    readonly children: ReactNode;
    readonly compact?: boolean;
    readonly streaming?: boolean;
};

export type AssistantBubbleExtrasProps = {
    readonly children: ReactNode;
};

function AssistantBubbleText({ children, compact, streaming }: AssistantBubbleTextProps) {
    return (
        <div
            className={`max-w-[92%] whitespace-pre-wrap ${compact ? 'text-xs' : 'text-[13px]'} text-black-900 [overflow-wrap:anywhere]`}
        >
            {children}
            {streaming && (
                <span
                    aria-hidden={true}
                    className="ml-[1px] inline-block h-[1em] w-[2px] animate-pulse bg-black-900 align-[-2px]"
                />
            )}
        </div>
    );
}

function AssistantBubbleExtras({ children }: AssistantBubbleExtrasProps) {
    return <div className="flex w-full max-w-[92%] flex-col gap-s">{children}</div>;
}

function hasSubcomponentChildren(children: ReactNode): boolean {
    return Children.toArray(children).some(
        (child) =>
            isValidElement(child) && (child.type === AssistantBubbleText || child.type === AssistantBubbleExtras),
    );
}

export function AssistantBubble({ children, timestamp, timeFormatter, compact, streaming }: AssistantBubbleProps) {
    const composed = hasSubcomponentChildren(children);

    return (
        <div className="group flex w-full flex-col items-start gap-s">
            {composed ? (
                children
            ) : (
                <AssistantBubbleText compact={compact} streaming={streaming}>
                    {children}
                </AssistantBubbleText>
            )}
            {timestamp !== undefined && timeFormatter && (
                <p className="m-0 text-[11px] text-gray-600 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                    {timeFormatter.format(new Date(timestamp))}
                </p>
            )}
        </div>
    );
}

AssistantBubble.Text = AssistantBubbleText;
AssistantBubble.Extras = AssistantBubbleExtras;
